import "aos/dist/aos.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import api from "../../../../../../api/api";
import { useAuth } from "../../../../../../features/auth/AuthContext";
import { deleteLessonById } from "../../../../../../state/actions/deleteLessonAction";
import { deleteAssignmentById } from "../../../../../../state/actions/deleteAssignmentAction";
import { alert_image } from "../../../../../../assets/images/images";
import MenuItem from "@mui/material/MenuItem";
import StyledMenu from "../../../../../../components/StyleMenu";
import { isGeneratedLessonName } from "../../../../../../utils/lessonName";
import "./LessonCard.css";

const GENERATE_LESSON_DESCRIPTION_PLACEHOLDER = "@$%^231#^#a5fgd%532^&4R3&";

const LESSON_NAME_MAX_LENGTH = 75;
const LESSON_NAME_MAX_LENGTH_ERROR =
  "Please don't enter more than 75 characters in Lesson Name.";

const LESSON_NAME_RESTRICTED_CHARS_ERROR =
  "Characters + % & # are not allowed in Lesson Name.";

const LessonCard = ({
  lesson,
  course,
  teacher_id,
  hideActions = true,
  roleFromState,
  accessControl,
  userRoleCourseRights,
  onNavigateNewLesson,
  onLessonsListRefresh,
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const globalState = useSelector((state) => state.globalState);
  const Admin = globalState?.role;

  const isGenerateLessonPlaceholder =
    lesson.description === GENERATE_LESSON_DESCRIPTION_PLACEHOLDER;
  const [isEditingLessonName, setIsEditingLessonName] = useState(false);
  const [draftLessonName, setDraftLessonName] = useState("");
  const [displayLessonName, setDisplayLessonName] = useState(
    () => lesson.name || "",
  );
  // A lesson still called "Lesson 1" has no name of its own yet — it is named
  // when it is created, in the generator, where the field is left empty for
  // exactly this reason. Renaming it from the card would put a title on a
  // lesson that does not exist, so the whole affordance is withdrawn: no
  // pencil, no click target, no keyboard entry point. Anything with a real
  // name stays editable as before.
  //
  // Declared after displayLessonName because it reads it — the name shown is
  // what the teacher is acting on, not the possibly stale lesson.name prop.
  const canEditLessonName =
    course?.my_role !== "viewer" && !isGeneratedLessonName(displayLessonName);

  /**
   * The lesson prop carries the name the parent last fetched, which is not the
   * name on screen the moment after a rename: saving updates displayLessonName
   * here and asks the parent to refetch, and the refetch is still in flight
   * while the card already reads "English 4".
   *
   * Create used to hand that prop straight to the generator, so it opened on
   * the pre-rename name. Sending the displayed name instead makes the hand-off
   * independent of when the refetch lands.
   */
  const withCurrentLessonName = (target) => ({
    ...target,
    name: displayLessonName || target?.name,
  });
  const [isSavingLessonName, setIsSavingLessonName] = useState(false);
  const lessonNameInputRef = useRef(null);
  const lessonNameEditWrapperRef = useRef(null);
  // Keeps the latest submit handler reachable from the document listener below.
  const submitLessonNameRef = useRef(null);
  const isSubmittingLessonNameRef = useRef(false);
  const [assignments, setAssignments] = useState([]);
  const dispatch = useDispatch();
  const deleteAssignmentState = useSelector((state) => state.deleteAssignment);
  const { isLoading, success } = useSelector((state) => state.deleteLesson);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuIconClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  // useEffect(() => {
  //   localStorage.removeItem("assignmentanalyticsdetails");
  //   api
  //     .get(`assignments/get-assignments-by-lesson/${lesson.lesson_id}/`, {
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Token ${token}`, // `Token ${token}`
  //       },
  //     })
  //     .then((response) => {
  //       if (response.status === 200) {
  //         setAssignments(response?.data?.data ?? []);
  //       }
  //     })
  //     .catch((error) => {
  //       setAssignments([]);
  //     });
  // }, [deleteAssignmentState.success, success]);

  const [deleteAssignment, setdeleteAssignment] = useState(false);
  const [deleteLesson, setdeleteLesson] = useState(false);

  const deleteCourseId = (id) => {
    dispatch(deleteLessonById(id, token));
  };

  const deleteAssignmentReq = (id) => {
    dispatch(deleteAssignmentById(id, token));
  };

  async function updateLessonName(lessonId, name) {
    return api.put(
      `lessons/rename-lesson/${lessonId}/`,
      { name },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
      },
    );
  }

  const cancelLessonNameEdit = useCallback(() => {
    setIsEditingLessonName(false);
    setDraftLessonName("");
  }, []);

  useEffect(() => {
    if (!isEditingLessonName) {
      setDisplayLessonName(lesson.name || "");
    }
  }, [lesson.lesson_id, lesson.name, isEditingLessonName]);

  useEffect(() => {
    if (isEditingLessonName && lessonNameInputRef.current) {
      lessonNameInputRef.current.focus();
    }
  }, [isEditingLessonName]);

  useEffect(() => {
    if (!isEditingLessonName) return;
    // Touch devices have no Enter key, so tapping outside commits the rename.
    // pointerdown fires once for both mouse and touch, avoiding a double submit.
    const handleOutsidePointerDown = (e) => {
      if (lessonNameEditWrapperRef.current?.contains(e.target)) return;
      submitLessonNameRef.current?.();
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [isEditingLessonName]);

  const beginEditLessonName = () => {
    if (!canEditLessonName || !lesson?.lesson_id) return;
    setDraftLessonName(displayLessonName);
    setIsEditingLessonName(true);
  };

  const submitLessonNameFromInput = async () => {
    if (!lesson?.lesson_id || !token) return;
    // Guards against the outside-tap and Enter paths overlapping mid-save.
    if (isSubmittingLessonNameRef.current) return;
    isSubmittingLessonNameRef.current = true;
    try {
      await runLessonNameSubmit();
    } finally {
      isSubmittingLessonNameRef.current = false;
    }
  };

  const runLessonNameSubmit = async () => {
    const trimmed = draftLessonName.trim();
    if (trimmed === "") {
      toast.error("Lesson name cannot be empty", {
        position: "top-right",
      });
      return;
    }
    if (/[+%&#]/.test(trimmed)) {
      toast.error(LESSON_NAME_RESTRICTED_CHARS_ERROR, {
        position: "top-right",
        toastId: "lesson-name-restricted-chars",
      });
      return;
    }
    if (trimmed.length > LESSON_NAME_MAX_LENGTH) {
      toast.error(LESSON_NAME_MAX_LENGTH_ERROR, {
        position: "top-right",
      });
      return;
    }
    if (trimmed === displayLessonName.trim()) {
      cancelLessonNameEdit();
      return;
    }
    setIsSavingLessonName(true);
    try {
      const res = await updateLessonName(lesson.lesson_id, trimmed);
      if (res?.status === 200 || res?.status === 201) {
        setDisplayLessonName(trimmed);
        cancelLessonNameEdit();
        onLessonsListRefresh?.();
        toast.success("Lesson name updated successfully.", {
          position: "top-right",
        });
      } else {
        toast.error("Could not update lesson name", {
          position: "top-right",
        });
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Could not update lesson name",
        { position: "top-right" },
      );
    } finally {
      setIsSavingLessonName(false);
    }
  };

  // Re-pointed on every render so the outside-tap listener always calls the
  // handler bound to the current draft value.
  submitLessonNameRef.current = submitLessonNameFromInput;

  const onPreviewLesson = (lesson) => {
    const key = `previewLessonData`;
    // withCurrentLessonName is applied here rather than at the call site so
    // every route into the generator carries the name now on the card, not the
    // one the parent last fetched.
    const data = { course, teacher_id, lesson: withCurrentLessonName(lesson) };

    sessionStorage.setItem(key, JSON.stringify(data));

    const url = `/NewLessons?lesson_id=${lesson.lesson_id}&course_id=${course.course_id}&teacher_id=${teacher_id}`;
    window.open(url, "_blank");
  };

  const onPreviewAssignment = (lesson) => {
    const key = `previewLessonData`;
    const data = {
      course,
      teacher_id,
      lesson: withCurrentLessonName(lesson),
      openFirstAssignment: true,
      targetAssignmentIndex: 0,
    };
    sessionStorage.setItem(key, JSON.stringify(data));
    const url = `/NewLessons?course_id=${course.course_id}&teacher_id=${teacher_id}&lesson_id=${lesson.lesson_id}&openFirstAssignment=true&targetAssignmentIndex=0&skip_lesson=true`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (success) {
      setdeleteLesson(false);
    }
    if (deleteAssignmentState.success) {
      setdeleteAssignment(false);
    }
  }, [deleteAssignmentState, success]);

  return (
    <div className="view-lessons-created-course-bg-wrapper teacher-lesson-row">
      <div className="view-lessons-created-course-wrapper">
        <div className="view-lessons-created-course-inner-prewview-container">
          <div className="view-lessons-created-course-inner-preview-title">
            {isEditingLessonName ? (
              <div
                ref={lessonNameEditWrapperRef}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  overflow: "auto",
                }}
              >
                <input
                  ref={lessonNameInputRef}
                  type="text"
                  className="lesson-card-inline-name-input"
                  style={{
                    flex: "none",
                    width: `${Math.max(draftLessonName.length, 1)}ch`,
                    minWidth: "2ch",
                    maxWidth: "100%",
                    border: "none",
                    background: "transparent",
                    boxShadow: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#000",
                    fontFamily: "inherit",
                    lineHeight: "inherit",
                    textTransform: "capitalize",
                    outline: "none",
                  }}
                  value={draftLessonName}
                  disabled={isSavingLessonName}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (/[+%&#]/.test(v)) {
                      toast.error(LESSON_NAME_RESTRICTED_CHARS_ERROR, {
                        position: "top-right",
                        toastId: "lesson-name-restricted-chars",
                      });
                    }
                    if (v.length > LESSON_NAME_MAX_LENGTH) {
                      toast.error(LESSON_NAME_MAX_LENGTH_ERROR, {
                        position: "top-right",
                      });
                      v = v.slice(0, LESSON_NAME_MAX_LENGTH);
                    }
                    setDraftLessonName(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitLessonNameFromInput();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      cancelLessonNameEdit();
                    }
                  }}
                  aria-label="Edit lesson name"
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor:
                    canEditLessonName && !course?.is_archived
                      ? "pointer"
                      : "default",
                }}
                onClick={
                  course?.my_role !== "viewer" && !course?.is_archived
                    ? beginEditLessonName
                    : undefined
                }
                onKeyDown={(e) => {
                  if (
                    canEditLessonName &&
                    !course?.is_archived &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault();
                    beginEditLessonName();
                  }
                }}
                role={
                  canEditLessonName && !course?.is_archived
                    ? "button"
                    : undefined
                }
                tabIndex={
                  canEditLessonName && !course?.is_archived ? 0 : undefined
                }
              >
                <p
                  style={{ margin: 0 }}
                  className="!max-w-[170px] md:!max-w-[370px]"
                >
                  {displayLessonName}
                </p>
                {canEditLessonName && !course?.is_archived && (
                  <i className="fa-solid fa-pencil" aria-hidden />
                )}
              </div>
            )}
            {/* {hideActions && (
              <div className="edit-course-dropdown-menu-container mobile-course-dropdown-menu-container">
                <button
                  type="button"
                  className="course-edit-or-delete-folder"
                  onClick={() => setdeleteLesson(true)}
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            )} */}
            {hideActions &&
              course?.my_role === "owner" &&
              !course?.is_archived && (
                <div className="edit-course-dropdown-menu-container mobile-course-dropdown-menu-container">
                  <div
                    className="edit-course-or-delete-course"
                    style={{ cursor: "pointer" }}
                    onClick={handleMenuIconClick}
                  >
                    <i className="fa-solid fa-ellipsis-vertical"></i>
                  </div>
                  <StyledMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        setdeleteLesson(true);
                      }}
                      disableRipple
                      sx={{
                        backgroundColor: "transparent !important",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <button
                        type="button"
                        className="course-edit-or-delete-folder"
                      >
                        <a href="#">
                          <i className="fa-solid fa-trash"></i>
                        </a>
                        <p>Delete Lesson</p>
                      </button>
                    </MenuItem>
                  </StyledMenu>
                </div>
              )}
          </div>
          <div className="view-lessons-created-course-inner-preview-btn-folder flex gap-3 flex-none justify-between pr-0 sm:gap-2 !lg:gap-2 sm:justify-start ">
            {course?.my_role === "viewer" && (
              <button
                type="button"
                className="Preview-lesson-btn"
                onClick={() => {
                  navigate(
                    `/course/${course?.course_id}/lesson/${lesson?.lesson_id}/view`,
                    {
                      state: { course, teacher_id, lesson },
                    },
                  );
                }}
              >
                View Lesson
              </button>
            )}
            {lesson?.assignment_count > 0 && (
              <div
                className={`assignments_count_details text-xs sm:text-base ${
                  lesson?.assignment_count === 0 ? "disabled" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (lesson?.assignment_count === 0) return;
                  const course_id = course?.course_id;
                  const lesson_id = lesson?.lesson_id;
                  navigate(`/course-assignment-list/${lesson_id}`, {
                    state: {
                      course,
                      teacher_id,
                      lesson,
                      string: "omkar here",
                      role: roleFromState,
                      accessControl,
                      userRoleCourseRights,
                    },
                  });
                }}
              >
                {`View Assignments (${lesson?.assignment_count})`}
              </div>
            )}
            {lesson?.lower_school && !isGenerateLessonPlaceholder && (
              <div
                className={`assignments_count_details text-xs sm:text-base`}
                onClick={(e) => {
                  e.preventDefault();
                  // if (lesson?.submission_count === 0) return;
                  const lesson_id = lesson?.lesson_id;
                  navigate(`/lesson-submissions/${lesson_id}`, {
                    state: { course, teacher_id, lesson },
                  });
                }}
              >
                {`View Submissions (${lesson?.submission_count})`}
                {/* {`View Submissions`} */}
              </div>
            )}

            {/* if skip_lesson is true, then show edit assignmennt */}

            {hideActions &&
              course?.my_role !== "viewer" &&
              !course?.is_archived &&
              (lesson?.is_skip ? (
                <button
                  type="button"
                  onClick={() =>
                    isGenerateLessonPlaceholder
                      ? onNavigateNewLesson?.(withCurrentLessonName(lesson))
                      : onPreviewAssignment(lesson)
                  }
                  className="Preview-lesson-btn"
                  style={{ width: "175px" }}
                >
                  {isGenerateLessonPlaceholder
                    ? // ? "Generate Lesson"
                      "Create"
                    : "Edit Lesson"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    isGenerateLessonPlaceholder
                      ? onNavigateNewLesson?.(withCurrentLessonName(lesson))
                      : onPreviewLesson(lesson)
                  }
                  className="Preview-lesson-btn"
                  style={{ width: "175px" }}
                >
                  {isGenerateLessonPlaceholder
                    ? // ? "Generate Lesson"
                      "Create"
                    : "Edit Lesson"}
                </button>
              ))}
            {hideActions &&
              (course?.my_role === "owner" || Admin === "Admin") &&
              !course?.is_archived && (
                <div className="edit-course-dropdown-menu-container desktop-course-dropdown-menu-container">
                  <button
                    type="button"
                    className="course-edit-or-delete-folder"
                    onClick={() => setdeleteLesson(true)}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              )}
          </div>
        </div>
      </div>
      {deleteLesson && (
        <form action="" className="delete-course-form">
          <div className="delete-course-form-container" data-aos="zoom-in">
            <div className="delete-course-alert-image">
              <img src={alert_image} alt="" />
            </div>
            <h2>Are you sure you want to delete this lesson?</h2>
            <div className="delete-course-form-btn-folder">
              <button
                type="button"
                onClick={() => setdeleteLesson(false)}
                className="not-delete-btn"
              >
                No
              </button>
              {isLoading ? (
                <div className="loading-spinner" />
              ) : (
                <button
                  type="button"
                  onClick={() => deleteCourseId(lesson.lesson_id)}
                  disabled={isLoading}
                  className="delete-btn"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {deleteAssignment && (
        <form action="" className="delete-course-form">
          <div className="delete-course-form-container" data-aos="zoom-in">
            <div className="delete-course-alert-image">
              <img src={alert_image} alt="" />
            </div>
            <h2>Are you sure you want to delete this assignment?</h2>
            <div className="delete-course-form-btn-folder">
              <button
                type="button"
                onClick={() => setdeleteAssignment(false)}
                className="not-delete-btn"
              >
                No
              </button>
              {deleteAssignmentState?.isLoading ? (
                <div className="loading-spinner" />
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    deleteAssignmentReq(assignments[0]?.assignment_id)
                  }
                  disabled={deleteAssignmentState?.isLoading}
                  className="delete-btn"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default LessonCard;
